import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus } from "lucide-react";
import { trpc } from "@/hooks/trpc";
import { toast } from "sonner";
import { FormatCard } from "./format-card";

export function FormatSection() {
  const utils = trpc.useUtils();
  const { data: formats = [] } = trpc.food.nutritionalFormats.list.useQuery();
  const { data: nutrientTypes = [] } = trpc.food.nutrientTypes.list.useQuery();

  const createFormat = trpc.food.nutritionalFormats.create.useMutation({
    onSuccess: () => utils.food.nutritionalFormats.list.invalidate(),
    onError: (e) => toast.error(e.message),
  });
  const deleteFormat = trpc.food.nutritionalFormats.delete.useMutation({
    onSuccess: () => utils.food.nutritionalFormats.list.invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const [newFormatName, setNewFormatName] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);

  const handleAddFormat = () => {
    if (!newFormatName.trim()) return;
    createFormat.mutate({ name: newFormatName.trim() });
    setNewFormatName("");
    nameRef.current?.focus();
  };

  return (
    <div className="space-y-3">
      <div className="space-y-0.5">
        <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Nutritional Formats
        </h2>
        <p className="text-xs text-muted-foreground">
          Display formats for nutritional values (e.g. EU, US) with conversion
          factors and daily reference values.
        </p>
      </div>

      {formats.length === 0 && (
        <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
          No formats yet. Add one below.
        </div>
      )}

      {formats.map((format) => (
        <FormatCard
          key={format.id}
          format={format}
          nutrientTypes={nutrientTypes}
          onDelete={() => deleteFormat.mutate(format.id)}
          isDeleting={deleteFormat.isPending}
        />
      ))}

      <div className="flex gap-2 pt-1">
        <Input
          ref={nameRef}
          value={newFormatName}
          onChange={(e) => setNewFormatName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddFormat(); } }}
          placeholder="e.g., EU Label"
          className="h-8 flex-1"
        />
        <Button
          size="sm"
          variant="outline"
          className="h-8"
          disabled={!newFormatName.trim() || createFormat.isPending}
          onClick={handleAddFormat}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add
        </Button>
      </div>
    </div>
  );
}
